/**
 * PillInviteManager
 *
 * Manages calendar invite participants created via family member pill taps.
 * Links pill-originated invitees to their family member for auto-removal on deselect.
 */

import type { EventParticipant } from '@/types/events';
import type { FamilyMember } from '@/types/family-member';
import type { CalendarMemberMapping } from '@/types/calendar-member-mapping';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import { FamilyMemberCalendarResolver } from '@/lib/family/FamilyMemberCalendarResolver';

export class PillInviteManager {
  /**
   * Resolve all calendar emails for a family member
   */
  static resolveEmails(
    memberId: string,
    mappings: CalendarMemberMapping[],
    calendars: DeviceCalendar[],
  ): string[] {
    return FamilyMemberCalendarResolver.getAllCalendarEmailsForMember(
      memberId,
      mappings,
      calendars,
    );
  }

  /**
   * Add a pill-linked invitee participant. Skips if email is already invited.
   */
  static addLinkedInvitee(
    participants: EventParticipant[],
    email: string,
    familyMember: FamilyMember,
  ): EventParticipant[] {
    const emailLower = email.toLowerCase();
    const alreadyInvited = participants.some(
      p => (p.role === 'invited' || p.role === 'organiser') &&
        p.email?.toLowerCase() === emailLower,
    );
    if (alreadyInvited) return participants;

    const invitee: EventParticipant = {
      id: `pill-invite-${familyMember.id}-${Date.now()}`,
      role: 'invited',
      name: familyMember.name,
      email,
      linkedFamilyMemberId: familyMember.id,
    };

    return [...participants, invitee];
  }

  /**
   * Remove all pill-linked invitees for a given family member
   */
  static removeLinkedInvitees(
    participants: EventParticipant[],
    familyMemberId: string,
  ): EventParticipant[] {
    return participants.filter(
      p => p.linkedFamilyMemberId !== familyMemberId,
    );
  }

  /**
   * Check if a pill-linked invitee exists for a given family member
   */
  static hasLinkedInvitee(
    participants: EventParticipant[],
    familyMemberId: string,
  ): boolean {
    return participants.some(p => p.linkedFamilyMemberId === familyMemberId);
  }
}
